import { FormOutlined } from '@ant-design/icons';
import AppBackButton from '@nxsys/components/AppBackButton';
import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import AppDescription from '@nxsys/components/AppDescription';
import AppIconCard from '@nxsys/components/AppIconCard';
import AppIconCurrency from '@nxsys/components/AppIconCurrency';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { InvoiceSystemTypeEnum } from '@nxsys/constants/AppEnums';
import {
  useGetInvoice,
  usePreviewInvoice,
  useUpdateInvoice,
} from '@nxsys/hooks/api/invoice/useInvoiceApi';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { UpdateInvoiceByIdParams } from '@nxsys/types/api';
import { Icons } from '@nxsys/types/icons';
import { Rate } from '@nxsys/types/models/invoice';
import { showCurrencyByCountry } from '@nxsys/utils';
import { showMessage } from '@nxsys/utils/message';
import { formatMoney } from '@nxsys/utils/number-helper';
import { Col, Descriptions, Divider, Row, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { toNumber } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import InvoiceForm, {
  InvoiceFormRef,
  InvoiceFormValues,
} from '../components/InvoiceForm';
import { vatTotalLabel } from './vat-total-label';

const { Text } = Typography;

const InvoiceDetail = () => {
  const { messages } = useTypedIntl();
  const { payrollCountry, isBureau } = useSelector(
    (state: AppState) => state.appStore,
  );

  const params = useParams();
  const navigate = useNavigateWithQuery();
  const {
    run: getInvoiceRun,
    value: invoiceValue,
    loading: isGetInvoicePending,
  } = useGetInvoice();

  const {
    value: updatedInvoiceValue,
    run: updateInvoiceRun,
    loading: isUpdateInvoicePending,
  } = useUpdateInvoice();

  const invoiceData = useMemo(
    () => updatedInvoiceValue?.data ?? invoiceValue?.data,
    [updatedInvoiceValue, invoiceValue],
  );

  const { run: previewInvoiceRun, loading: isDownloadInvoicePending } =
    usePreviewInvoice();

  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (params.id) {
      getInvoiceRun({ id: params.id });
    }
  }, [params.id]);

  const formRef = useRef<InvoiceFormRef>(null);

  const handleGoBack = () => navigate('/invoices');

  const toggleEditing = () => setIsEditing((v) => !v);

  const renderTitle = useCallback(() => {
    return (
      <div className="flex items-center gap-x-4">
        <AppBackButton onClick={handleGoBack} />
        <div className="flex items-center gap-x-3">
          <AppPageTitle
            separator=""
            prefix={invoiceData?.invoiceNo ?? ''}
            title=""
          />
        </div>
      </div>
    );
  }, [messages, invoiceData]);

  const renderRightContent = useCallback(() => {
    if (!isEditing) {
      return (
        <div className="flex items-center gap-2">
          <AppButton
            variant="outlined"
            type="primary"
            name={messages['common.download']}
            onClick={handleDownload}
            loading={isDownloadInvoicePending}
            disabled={isDownloadInvoicePending}
            leftIcon={<Icons.IconDownload color="#00795B" />}
          />
          <AppButton
            type="primary"
            name={messages['common.edit']}
            leftIcon={<FormOutlined />}
            onClick={toggleEditing}
            disabled={isGetInvoicePending}
          />
        </div>
      );
    }
    return (
      <div className="flex items-center gap-2">
        <AppButton
          variant="outlined"
          type="primary"
          name={messages['common.cancel']}
          onClick={toggleEditing}
        />
        <AppButton
          type="primary"
          name={messages['common.save']}
          onClick={() => formRef.current?.submit()}
          loading={isUpdateInvoicePending}
          disabled={isUpdateInvoicePending}
        />
      </div>
    );
  }, [
    messages,
    isEditing,
    isGetInvoicePending,
    isUpdateInvoicePending,
    isDownloadInvoicePending,
    formRef,
  ]);

  const handleSubmit = async (values: InvoiceFormValues) => {
    if (!invoiceData?.id) return;
    const params: UpdateInvoiceByIdParams = {
      id: invoiceData.id,
      rates:
        values.rates?.map((rate) => ({
          ...rate,
          rateCode: (rate.rateCode as any)?.value ?? '',
          vatDetails: (rate.vatDetails as any)?.value ?? '',
          units: toNumber(rate.units),
          net: toNumber(rate.net),
          total: toNumber(rate.total),
          vatRate: rate.vatRate ?? '',
          includeInCisCalculation: true,
        })) ?? [],
    };
    try {
      const res = await updateInvoiceRun(params);
      if (res?.success) {
        setIsEditing(false);
        await getInvoiceRun({ id: invoiceData.id });
      } else {
        showMessage({
          type: 'error',
          content: messages['common.error'],
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const detailItems = useMemo(
    () => [
      ...(!isBureau
        ? [
            {
              label: messages['common.agencyName'],
              value: invoiceData?.agencyRef?.name ?? '',
            },
          ]
        : []),
      {
        label: isBureau ? messages['common.bureau.companyName'] : messages['common.companyName'],
        value: invoiceData?.companyRef?.companyName ?? '',
      },
      {
        label: messages['common.reference'],
        value: invoiceData?.reference ?? '',
      },
      {
        label: messages['common.invoiceNumber'],
        value: invoiceData?.invoiceNo ?? '',
      },
      {
        label: messages['common.issueDate'],
        value: invoiceData?.invoicedDate
          ? moment(invoiceData?.invoicedDate).format('DD/MM/YYYY')
          : '',
      },
      {
        label: messages['common.dueDate'],
        value:
          invoiceData?.dueDateTimesheet || invoiceData?.endDate
            ? moment(
                invoiceData?.dueDateTimesheet || invoiceData?.endDate,
              ).format('DD/MM/YYYY')
            : '',
      },
    ],
    [invoiceData],
  );

  const rateColumns: ColumnsType<Rate> = [
    {
      key: 'rateCode',
      title: messages['assignment.rate.rateCode'],
      dataIndex: 'rateCode',
    },
    {
      key: 'description',
      title: messages['assignment.rate.description'],
      dataIndex: 'description',
    },
    {
      key: 'rate',
      title: messages['assignment.rate.rate'],
      dataIndex: 'rate',
      render: (rate: string) =>
        formatMoney(toNumber(rate), showCurrencyByCountry(payrollCountry)),
    },
    {
      key: 'unit',
      title: messages['common.unit'],
      dataIndex: 'units',
      width: 100
    },
    {
      key: 'net',
      title: messages['common.net'],
      dataIndex: 'net',
      render: (rate: string) =>
        formatMoney(toNumber(rate), showCurrencyByCountry(payrollCountry)),
    },
    {
      key: 'vatDetails',
      title: messages['assignment.rate.vatRate'],
      dataIndex: 'vatDetails',
      width: 200,
    },
    {
      key: 'total',
      title: messages['common.total'],
      dataIndex: 'total',
      render: (rate: string) =>
        formatMoney(toNumber(rate), showCurrencyByCountry(payrollCountry)),
    },
  ];

  const handleDownload = async () => {
    if (!invoiceData?.id) return;
    try {
      const response = await previewInvoiceRun({ id: invoiceData.id });
      if (response?.data) {
        window.open(response.data, '_blank');
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <AppsContainer
      fullView
      type="bottom"
      title={renderTitle()}
      rightContent={renderRightContent()}
      loading={isGetInvoicePending}
    >
      {!isEditing ? (
        <>
          <AppCard className="p-[16px]">
            <AppIconCard
              icon={<Icons.IconInvoice />}
              title={messages['common.invoice']}
            />
            <Divider className="mt-[12px] mb-[16px]" />
            <AppDescription
              column={1}
              labelStyle={{ width: 200 }}
              bordered={false}
              colon={false}
              size="middle"
            >
              {detailItems.map((it, idx) => (
                <Descriptions.Item key={idx} label={it.label}>
                  <Text>{it.value}</Text>
                </Descriptions.Item>
              ))}
            </AppDescription>
          </AppCard>
          <Row className="mt-4" gutter={[18, 18]}>
            <Col sm={24} md={18}>
              {invoiceData?.invoiceType === InvoiceSystemTypeEnum.DEFAULT && (
                <AppCard>
                  <div className="p-[16px] pb-[12px]">
                    <AppIconCard
                      icon={<AppIconCurrency />}
                      title={messages['common.rates']}
                    />
                  </div>
                  <Divider className="my-0" />
                  <AppTableContainer
                    data={invoiceData?.rates ?? []}
                    columns={rateColumns}
                    loading={false}
                    pagination={false}
                    page={0}
                    total={invoiceData?.rates?.length ?? 0}
                    pageSize={0}
                    scroll={{ x: 1000, y: 700 }}
                  />
                </AppCard>
              )}
            </Col>
            <Col sm={24} md={6} className="max-sm:w-full">
              <AppCard>
                <div className="space-y-3 p-4">
                  <div className="flex justify-between items-center">
                    <Typography.Text>Net</Typography.Text>
                    <Typography.Text>
                      {formatMoney(toNumber(invoiceData?.totalNet), showCurrencyByCountry(payrollCountry))}
                    </Typography.Text>
                  </div>
                  <div className="flex justify-between items-center">
                    <Typography.Text>Total No VAT</Typography.Text>
                    <Typography.Text>
                      {formatMoney(0, showCurrencyByCountry(payrollCountry))}
                    </Typography.Text>
                  </div>
                  <div className="flex justify-between items-center">
                    <Typography.Text>
                      {vatTotalLabel(invoiceData?.rates)}
                    </Typography.Text>
                    <Typography.Text>
                      {formatMoney(toNumber(invoiceData?.totalVat), showCurrencyByCountry(payrollCountry))}
                    </Typography.Text>
                  </div>
                  <Divider />
                  <div className="flex justify-between items-center">
                    <Typography.Text>
                      {messages['common.total']}
                    </Typography.Text>
                    <Typography.Text>
                      {formatMoney(toNumber(invoiceData?.totalPrices), showCurrencyByCountry(payrollCountry))}
                    </Typography.Text>
                  </div>
                </div>
              </AppCard>
            </Col>
          </Row>
        </>
      ) : (
        <InvoiceForm
          formRef={formRef}
          onSubmit={handleSubmit}
          defaultValues={{
            agencyRef: {
              label: invoiceData?.agencyRef?.name ?? '',
              value: invoiceData?.agencyRef?.id ?? '',
            },
            companyRef: {
              label: invoiceData?.companyRef?.companyName ?? '',
              value: invoiceData?.companyRef?.id ?? '',
            },
            endDate:
              invoiceData?.dueDateTimesheet || invoiceData?.endDate
                ? moment(
                    invoiceData?.dueDateTimesheet || invoiceData?.endDate,
                  ).toISOString()
                : null,
            invoicedDate: invoiceData?.invoicedDate
              ? moment(invoiceData.invoicedDate).toISOString()
              : null,
            reference: invoiceData?.reference,
            rates:
              invoiceData?.rates?.map((rate) => ({
                ...rate,
                rateCode: { label: rate.rateCode, value: rate.rateCode },
                vatDetails: { label: rate.vatDetails, value: rate.vatDetails },
                vatCode:
                  rate.vatCode ??
                  // vatDetails format: "<desc> (<VATCode>) - <VATRate>" —
                  // grab the parenthesised group right before " - ".
                  rate.vatDetails?.match(/\(([^()]+)\)\s*-/)?.[1] ??
                  '',
                net: rate.net.toString(),
                rate: rate.rate.toString(),
                total: rate.total.toString(),
                units: rate.units.toString(),
                vatRate: rate.vatRate,
              })) ?? [],
          }}
          isEdit={true}
        />
      )}
    </AppsContainer>
  );
};

export default InvoiceDetail;
